import PropTypes from "prop-types";
import SourceFlowImage from "@sourceflow-uk/sourceflowimage";
import imagesMetaData from "@/../.sourceflow/image_metadata.json";

/**
 * Renders a SourceFlow CMS image, rewriting preview-domain sources to the live domain.
 *
 * `fetchpriority` is intentionally lowercase so React 18 forwards it to the DOM without warning.
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string|{path?: string, src?: string}} [props.img]
 * @param {string} [props.size]
 * @param {string} [props.alt]
 * @param {"lazy"|"eager"} [props.loading]
 * @param {"high"|"low"|"auto"} [props.fetchpriority]
 * @returns {JSX.Element|null}
 */
export default function Image({ className, img, size, alt, loading, fetchpriority }) {
  const transformUrl = (url) => {
    if (url) {
      return url.replace("https://gravitas-group.sites.sourceflow.co.uk", "https://www.gravitasgroup.com");
    }
    return url;
  };

  const transformedMetaData = Object.keys(imagesMetaData).reduce((acc, key) => {
    acc[key] = {
      ...imagesMetaData[key],
      src: transformUrl(imagesMetaData[key].src),
    };
    return acc;
  }, {});

  if (!img) {
    return null;
  }

  if (typeof img === "string") {
    return (
      <SourceFlowImage
        className={className}
        src={transformUrl(img)}
        size={size}
        alt={alt}
        loading={loading}
        fetchpriority={fetchpriority}
      />
    );
  }

  if (typeof img === "object") {
    return (
      <SourceFlowImage
        className={className}
        path={img.path ?? null}
        src={transformUrl(img.src ?? null)}
        size={size}
        alt={alt}
        loading={loading}
        fetchpriority={fetchpriority}
        imagesMetaData={transformedMetaData}
      />
    );
  }

  return null;
}

Image.defaultProps = {
  className: "",
  img: null,
  size: null,
  alt: "",
  loading: "lazy",
  fetchpriority: undefined,
};

Image.propTypes = {
  className: PropTypes.string,
  img: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      path: PropTypes.string,
      src: PropTypes.string,
    }),
  ]),
  size: PropTypes.string,
  alt: PropTypes.string,
  loading: PropTypes.oneOf(["lazy", "eager"]),
  fetchpriority: PropTypes.oneOf(["high", "low", "auto"]),
};